import { onMounted, onUnmounted } from 'vue'

export interface ShortcutOption {
  key: string // e.g. 'd', 'Enter', 'Escape', '?'
  alt?: boolean
  ctrl?: boolean
  shift?: boolean
  handler: (event: KeyboardEvent) => void
  allowInInputs?: boolean
  description?: string
  category?: string
}

export function useKeyboardShortcuts(shortcuts: ShortcutOption[]) {
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!event || typeof event.key !== 'string') return

    // Determine if user is currently focusing an input field
    const activeElement = document.activeElement
    const isInputActive =
      activeElement &&
      (activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.getAttribute('contenteditable') === 'true')

    for (const shortcut of shortcuts) {
      if (!shortcut || typeof shortcut.key !== 'string') continue

      // Match key (case-insensitive for letters)
      const keyMatches =
        event.key.toLowerCase() === shortcut.key.toLowerCase()

      const altMatches = !!shortcut.alt === event.altKey
      const ctrlMatches = !!shortcut.ctrl === event.ctrlKey
      const shiftMatches = !!shortcut.shift === event.shiftKey

      if (keyMatches && altMatches && ctrlMatches && shiftMatches) {
        // If in an input, skip unless explicitly allowed (e.g. submit on Ctrl+Enter, focus shortcuts)
        if (isInputActive && !shortcut.allowInInputs) {
          continue
        }

        // Trigger action and prevent browser defaults (e.g. Alt+D shouldn't highlight address bar on some OS/browsers if we want)
        event.preventDefault()
        event.stopPropagation()
        shortcut.handler(event)
        break // Stop executing other matching shortcuts
      }
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown)
  })
}
